import {
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import {
	CellHoverChangedEvent,
	OptionChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import {
	BehaviorSubject,
	concat,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	Subject,
	switchMap,
	takeUntil,
	tap,
	withLatestFrom,
	zip,
} from 'rxjs';

import { ElementPermission } from '../../../core/domain/permission-types';
import {
	DcDxGrids,
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZES,
	DxFilterField,
	DxOperations,
	DxSortField,
	StringFilterOperations,
} from '../../../core/dx-types';
import {
	DxListsStorage,
	IDxList,
	StoredColumn,
} from '../../../core/services/dx-lists.storage';
import { DxGridUtil } from '../../../core/utils/dx-grid.util';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { SubscriptionStatus } from '../../domain/subscription.types';
import {
	activateBulkSubscription,
	activateBulkSubscriptionSuccess,
	activateSubscription,
	clearBulkSubscriptionData,
	clearBulkSubscriptionDataSuccess,
	clearSubscriptionData,
	deactivateBulkSubscription,
	deactivateBulkSubscriptionSuccess,
	deactivateSubscription,
	deleteBulkSubscription,
	deleteSubscription,
	deleteSubscriptionSuccess,
	getSubscriptions,
	initializeSubscriptionOnList,
	initSubscriptions,
	navigateToSubscriptionDetails,
	SubscriptionSelector,
	updateSubscriptionDataOnList,
} from '../../store';
import {
	ISubscriptionConfirmationModalData,
	SubscriptionActionConfirmPopupComponent,
	SubscriptionActionEnum,
} from '../subscription-action-confirm-popup/subscription-action-confirm-popup.component';
import { DxSubscriptionListProvider } from './dx-subscription-list.provider';
import { SubscriptionItemEntity } from './subscription-item.entity';
import {
	ISubscriptionListExtra,
	SubscriptionListEntity,
} from './subscription-list.entity';
import { SubscriptionListProvider } from './subscription-list.provider';

@Component({
	selector: 'app-subscription-list',
	templateUrl: './subscription-list.component.html',
	styleUrls: ['./subscription-list.component.scss'],
	inputs: ['datablockId'],
	providers: [SubscriptionListProvider, DxSubscriptionListProvider],
})
export class SubscriptionListComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly HdIcons = HdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly DxOperations = DxOperations;
	protected readonly ActionIcons = ActionIcons;
	protected readonly SubscriptionStatus = SubscriptionStatus;
	protected readonly ElementPermission = ElementPermission;
	protected readonly StringFilterOperations = StringFilterOperations;

	public allowedPageSizes = [DEFAULT_PAGE_SIZES[0]];
	public currentlyHovered = -1;
	public selectionCount = 0;
	public panelActions: Array<PanelAction> = [];
	public toggleActions = false;
	public statusHeaderFilter = [
		{
			text: $localize`:i18n=@@general.status.available:`,
			value: SubscriptionStatus.Available,
		},
		{
			text: $localize`:i18n=@@general.status.unavailable:`,
			value: SubscriptionStatus.UnAvailable,
		},
		{
			text: $localize`:i18n=@@general.status.unpublished:`,
			value: SubscriptionStatus.Unpublish,
		},
		{
			text: $localize`:i18n=@@general.status.error:`,
			value: SubscriptionStatus.Error,
		},
	];

	public dataSource: CustomStore | null = null;

	public vo$: Observable<{
		list: SubscriptionListEntity;
		size: number;
		page: number;
		hasCreatePermission: boolean;
	}>;

	public loadState = (): IDxList | undefined => {
		const dxList = this.dxStorage.getListState(this.dxLocalStorageKey);

		if (dxList?.columns) {
			const sortedFields = DxGridUtil.generateSortFields(dxList.columns);
			this.sortedFields$.next(sortedFields);
		}
		this.onGridReady$.next();
		return this.dxStorage.getListState(this.dxLocalStorageKey);
	};

	public saveState = async (state: IDxList): Promise<void> => {
		const page = await firstValueFrom(this.page$);

		if (page !== state.pageIndex) {
			this.page$.next(state.pageIndex);
		}

		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@Output()
	public emitSubscriptionCount = new EventEmitter<number>();

	@Output()
	public createSubscriptionAction = new EventEmitter<void>();

	@ViewChild(DxDataGridComponent)
	public readonly grid: DxDataGridComponent | null = null;

	private readonly datablockId$ = new ReplaySubject<number | null>(1);

	private readonly page$ = new BehaviorSubject<number>(DEFAULT_PAGE_INDEX);
	private readonly size$ = new BehaviorSubject<number>(DEFAULT_PAGE_SIZES[0]);
	private readonly search$ = new BehaviorSubject<string>('');
	private readonly filteredFields$ = new BehaviorSubject<
		Array<DxFilterField<SubscriptionItemEntity>>
	>([]);
	private readonly sortedFields$ = new BehaviorSubject<Array<DxSortField>>([]);
	private readonly filterPanelEnabled$ = new BehaviorSubject<boolean>(true);

	private readonly onGridReady$ = new Subject<void>();
	private readonly manualTrigger$ = new BehaviorSubject<boolean>(false);
	private readonly isContentReadySubject$ = new Subject<boolean>();

	public constructor(
		private readonly selector: SubscriptionSelector,
		private readonly store: Store,
		private readonly subscriptionListProvider: SubscriptionListProvider,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService,
		private readonly dxSubListProvider: DxSubscriptionListProvider,
		private readonly action$: Actions,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'subscription-list';
		this.dataSource = this.dxSubListProvider.createDxStore();

		const list$ = this.selector.getSubscriptions$().pipe(
			withLatestFrom(this.selector.getTotalSubscriptions$()),
			tap(([, total]) => this.emitSubscriptionCount.next(total)),
			map(([elements, total]) =>
				this.subscriptionListProvider.setListElements(elements, total)
			),
			tap((list) => {
				this.grid?.instance.refresh(true);
				this.dxSubListProvider.updateData(list);
			})
		);

		this.vo$ = combineAll({
			list: list$,
			size: this.size$,
			page: this.page$,
			hasCreatePermission: this.selector.getHasCreatePermission$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
		this.handleSubscription();
	}

	public navigateToSubscriptionCreate(): void {
		this.createSubscriptionAction.emit();
	}

	public navigateToDetails(subscription: SubscriptionItemEntity): void {
		if (!subscription.id) {
			return;
		}

		this.store.dispatch(
			navigateToSubscriptionDetails({
				subscriptionId: subscription.id,
			})
		);
	}

	public async deleteSelected(
		selectedItem: SubscriptionItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRows =
				this.grid.instance.getSelectedRowsData() as Array<SubscriptionItemEntity>;

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.delete.header:`,
					subTitle: '',
					subscriptionId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: SubscriptionActionEnum.Delete,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				deleteSubscription({
					subscriptionId: selected[0],
				})
			);
		}

		return this.store.dispatch(
			deleteBulkSubscription({
				subscriptionIds: selected,
			})
		);
	}

	public async activateSelected(
		selectedItem: SubscriptionItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}

			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<SubscriptionItemEntity>
			).filter((s) => s.status === SubscriptionStatus.UnAvailable);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.activate.header:`,
					subTitle: '',
					subscriptionId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: SubscriptionActionEnum.Activate,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				activateSubscription({
					subscriptionId: selected[0],
					inViewMode: false,
				})
			);
		}

		return this.store.dispatch(
			activateBulkSubscription({
				subscriptionIds: selected,
			})
		);
	}

	public async deactivateSelected(
		selectedItem: SubscriptionItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}

			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<SubscriptionItemEntity>
			).filter((s) => s.status === SubscriptionStatus.Available);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.deactivate.header:`,
					subTitle: '',
					subscriptionId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: SubscriptionActionEnum.Deactivate,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				deactivateSubscription({
					subscriptionId: selected[0],
					inViewMode: false,
				})
			);
		}

		return this.store.dispatch(
			deactivateBulkSubscription({
				subscriptionIds: selected,
			})
		);
	}

	public async clearDataSelected(
		selectedItem: SubscriptionItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}

			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<SubscriptionItemEntity>
			).filter((s) => s.status !== SubscriptionStatus.Unpublish);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title:
						selected.length === 1
							? $localize`:i18n=@@subscription.action.confirmation.popup.clearData.header:`
							: $localize`:i18n=@@subscription.action.confirmation.popup.clearData.multiple.header:`,
					subTitle: '',
					subscriptionId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: SubscriptionActionEnum.ClearData,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				clearSubscriptionData({
					subscriptionId: selected[0],
					inViewMode: false,
				})
			);
		}

		return this.store.dispatch(
			clearBulkSubscriptionData({
				subscriptionIds: selected,
			})
		);
	}

	public async updateDataForSelected(
		selectedItem: SubscriptionItemEntity | undefined = undefined,
		event?: Event
	): Promise<void> {
		if (event) {
			event.stopPropagation();
		}

		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<SubscriptionItemEntity>
			).filter((s) => s.status !== SubscriptionStatus.Unpublish);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title:
						selected.length === 1
							? $localize`:i18n=@@subscription.action.confirmation.popup.updateData.header:`
							: $localize`:i18n=@@subscription.action.confirmation.popup.updateData.multiple.header:`,
					subTitle: '',
					subscriptionId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: SubscriptionActionEnum.UpdateData,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				updateSubscriptionDataOnList({
					subscriptionId: selected[0],
					operationType: 'update-data',
				})
			);
		}

		return selected.forEach((id) =>
			this.store.dispatch(
				updateSubscriptionDataOnList({
					subscriptionId: id,
					operationType: 'update-data',
				})
			)
		);
	}

	public async initializeForSelected(
		selectedItem: SubscriptionItemEntity | undefined = undefined,
		event?: Event
	): Promise<void> {
		if (event) {
			event.stopPropagation();
		}

		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRows = (
				this.grid.instance.getSelectedRowsData() as Array<SubscriptionItemEntity>
			).filter((s) => s.status === SubscriptionStatus.Unpublish);

			if (selectedRows.length === 0) {
				return;
			}

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<ISubscriptionConfirmationModalData>(
				SubscriptionActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.action.confirmation.popup.initialize.header:`,
					subTitle: '',
					subscriptionId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: SubscriptionActionEnum.Initialize,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}

		if (selected.length === 1) {
			return this.store.dispatch(
				initializeSubscriptionOnList({
					subscriptionId: selected[0],
					operationType: 'initialize',
				})
			);
		}

		return selected.forEach((id) =>
			this.store.dispatch(
				initializeSubscriptionOnList({
					subscriptionId: id,
					operationType: 'initialize',
				})
			)
		);
	}

	public refresh(): void {
		this.manualTrigger$.next(true);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		const searchValue = searchTerm.trim();
		this.grid.instance.searchByText(searchValue);
		this.search$.next(searchValue);
	}

	public onSelectionChange(event: SelectionChangedEvent): void {
		this.toggleActions = event.selectedRowsData.length > 0;
		this.selectionCount = event.selectedRowsData.length;
	}

	public onContentReady(): void {
		this.isContentReadySubject$.next(true);
	}

	public onGridOptionChange(event: OptionChangedEvent): void {
		if (event.name === 'hoveredElement') {
			return;
		}

		if (
			event.name !== 'paging' &&
			event.name !== 'filterValue' &&
			event.name !== 'filterPanel' &&
			event.name !== 'columns'
		) {
			return;
		}

		const { fullName: action, value } = event;

		if (action.includes('pageSize')) {
			this.page$.next(DEFAULT_PAGE_INDEX);
			this.size$.next(value);
		} else if (action.includes('pageIndex')) {
			this.page$.next(value);
		} else if (action === 'filterValue') {
			let filteredFields: Array<DxFilterField<SubscriptionItemEntity>> = [];
			if (value) {
				filteredFields = DxGridUtil.parseFilters<SubscriptionItemEntity>(value);
			}

			this.filteredFields$.next(filteredFields);
			this.page$.next(DEFAULT_PAGE_INDEX);
		} else if (action.includes('columns') && action.endsWith('sortOrder')) {
			const columns = event.component.getVisibleColumns() as Array<
				Partial<StoredColumn>
			>;

			const sortedFields = DxGridUtil.generateSortFields(columns);

			this.sortedFields$.next(sortedFields);
		} else if (action.includes('filterEnabled')) {
			this.filterPanelEnabled$.next(value);
		}
	}

	private triggerInit(): void {
		this.store.dispatch(initSubscriptions());

		concat(of(null), this.toObservable<number>('datablockId'))
			.pipe(
				takeUntil(this.onDestroy$),
				tap((datablockId) => {
					this.dxLocalStorageKey = datablockId
						? DcDxGrids.subscriptionListDatablockEmbedded
						: DcDxGrids.SubscriptionListStandalone;

					let cols = this.subscriptionListProvider.getAvailableColumns(
						datablockId !== null
					);
					cols = this.dxStorage.synchronize<
						ISubscriptionListExtra,
						SubscriptionItemEntity
					>(this.dxLocalStorageKey, cols);

					this.subscriptionListProvider.setList(cols);
					this.datablockId$.next(datablockId);
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	private handleSubscription(): void {
		this.action$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					deleteSubscriptionSuccess,
					activateBulkSubscriptionSuccess,
					deactivateBulkSubscriptionSuccess,
					clearBulkSubscriptionDataSuccess
				),
				tap(() => {
					this.modalService.closeActiveModal({
						reset: true,
					});
					this.manualTrigger$.next(true);
				})
			)
			.subscribe();

		zip(this.onGridReady$, this.datablockId$)
			.pipe(
				switchMap(([, datablock]) =>
					combineAll({
						datablock,
						manualTrigger: this.manualTrigger$,
						search: this.search$.pipe(debounceTime(300)),
						gridState: combineAll({
							filteredFields: this.filteredFields$,
							sortedFields: this.sortedFields$,
							page: this.page$,
							size: this.size$,
							filterPanelEnabled: this.filterPanelEnabled$,
						}).pipe(debounceTime(500)),
					})
				),
				takeUntil(this.onDestroy$),
				tap(({ datablock, search, gridState }) => {
					const {
						filteredFields,
						sortedFields,
						page,
						size,
						filterPanelEnabled,
					} = gridState;
					const updatedFilterFields: Array<
						DxFilterField<SubscriptionItemEntity>
					> = datablock
						? [
								...filteredFields,
								{
									field: 'datablockId',
									operator: DxOperations.Equal,
									value: `${datablock}`,
								},
						  ]
						: filteredFields;

					this.store.dispatch(
						getSubscriptions({
							size,
							page,
							search,
							filteredFields: filterPanelEnabled
								? updatedFilterFields
								: new Array<DxFilterField<SubscriptionItemEntity>>(),
							sortedFields,
						})
					);
				})
			)
			.subscribe();

		this.onGridReady$
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(500),
				tap(() => {
					this.grid?.instance.updateDimensions();
				})
			)
			.subscribe();
	}
	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.Delete,
			ActionIcons.Share,
			ActionIcons.ShareOff,
			ActionIcons.Output,
			ActionIcons.Input,
			ActionIcons.Publish,
			ActionIcons.ColumnExpositionChooser,
		];

		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@expositions.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.updateDataForSelected(),
				iconName: actionMap.get(ActionIcons.Input)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.list.action.updateData:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.activateSelected(),
				iconName: actionMap.get(ActionIcons.Share)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.list.action.activate:`,
				typed: 'plain-context-action',
			},
			{
				order: 3,
				command: (): Promise<void> => this.deactivateSelected(),
				iconName: actionMap.get(ActionIcons.ShareOff)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.list.action.deactivate:`,
				typed: 'plain-context-action',
			},
			{
				order: 4,
				command: (): Promise<void> => this.initializeForSelected(),
				iconName: actionMap.get(ActionIcons.Publish)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.list.action.initialize:`,
				typed: 'plain-context-action',
			},
			{
				order: 5,
				command: (): Promise<void> => this.clearDataSelected(),
				iconName: actionMap.get(ActionIcons.Output)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@subscription.list.action.clearData:`,
				typed: 'plain-context-action',
			},
			{
				order: 6,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: $localize`:i18n=@@subscription.list.action.delete:`,
				typed: 'grouped-context-action',
			},
		];
	}
}
